'use client';

import {getEnv} from '@config/getEnv';
import {IS_DEV} from '@config/isDev';
import {FetchNetworkException} from '@core/exceptions';
import * as Sentry from '@sentry/nextjs';
import {
    keepPreviousData,
    MutationCache,
    QueryCache,
    QueryClient,
    QueryClientProvider,
} from '@tanstack/react-query';
import {ReactQueryDevtools} from '@tanstack/react-query-devtools';
import {FC, PropsWithChildren, useState} from 'react';

export const QueryProvider: FC<PropsWithChildren> = ({children}) => {
    const handleError = (error: Error, ...rest: unknown[]) => {
        Sentry.captureException(error, {
            extra: {queryParams: rest},
        });
    };

    const [queryClient] = useState(
        () =>
            new QueryClient({
                queryCache: new QueryCache({
                    onError: handleError,
                }),
                mutationCache: new MutationCache({
                    onError: handleError,
                }),
                defaultOptions: {
                    queries: {
                        retry: (failureCount, error) => {
                            if (error instanceof FetchNetworkException) {
                                return 3 - failureCount > 0;
                            }
                            return false;
                        },
                        staleTime: Infinity,
                        placeholderData: keepPreviousData,
                        throwOnError: error => {
                            if (error.message === 'NEXT_REDIRECT') {
                                return true;
                            }
                            return false;
                        },
                    },
                    mutations: {
                        retry: (failureCount, error) => {
                            if (error instanceof FetchNetworkException) {
                                return 3 - failureCount > 0;
                            }
                            return false;
                        },
                        throwOnError: error => {
                            if (error.message === 'NEXT_REDIRECT') {
                                return true;
                            }
                            return false;
                        },
                    },
                },
            }),
    );

    return (
        <QueryClientProvider client={queryClient}>
            {children}
            {(IS_DEV || getEnv('NEXT_PUBLIC_DEVTOOLS')) && (
                <ReactQueryDevtools initialIsOpen={false} />
            )}
        </QueryClientProvider>
    );
};
